import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import Sidebar from './Sidebar';
import Header from './Header';
import AmbientBackground from './AmbientBackground';
import MobileTabBar from './MobileTabBar';

/**
 * Glass surfaces whose highlight follows the pointer - the dashboard KPI tiles' Liquid Glass light,
 * on every glass card of every page (index.css: GLASS LIGHT). One delegated listener for the whole
 * main area instead of handlers on each card.
 */
const GLASS_SURFACE = [
  '.liquid-glass',
  ':is(.rounded-xl, .rounded-2xl, .rounded-lg).border:is(.bg-card, .bg-white)',
  '.app-shell main > .h-full.p-0 > div:first-child > div',   // Trace: conversation list
  '.app-shell main > .h-full.p-0 > div:last-child',          // Trace: chat panel
].join(', ');

/** The outermost glass surface under the pointer (nested insets carry no light of their own). */
function outermostSurface(target, root) {
  let card = target instanceof Element ? target.closest(GLASS_SURFACE) : null;
  if (!card || !root.contains(card)) return null;
  for (let up = card.parentElement?.closest(GLASS_SURFACE); up && root.contains(up);
    up = up.parentElement?.closest(GLASS_SURFACE)) {
    card = up;
  }
  return card;
}

function useGlassLight() {
  const mainRef = useRef(null);
  const current = useRef(null);
  const frame = useRef(0);
  const last = useRef(null);

  const clear = useCallback(() => {
    const el = current.current;
    if (el) {
      el.style.removeProperty('--lg-x');
      el.style.removeProperty('--lg-y');
      el.removeAttribute('data-lit');
    }
    current.current = null;
  }, []);

  const apply = useCallback(() => {
    frame.current = 0;
    const e = last.current;
    const root = mainRef.current;
    if (!e || !root) return;
    const card = outermostSurface(e.target, root);
    if (card !== current.current) clear();
    if (!card) return;
    const r = card.getBoundingClientRect();
    card.style.setProperty('--lg-x', `${((e.clientX - r.left) / r.width) * 100}%`);
    card.style.setProperty('--lg-y', `${((e.clientY - r.top) / r.height) * 100}%`);
    card.setAttribute('data-lit', '');
    current.current = card;
  }, [clear]);

  const onPointerMove = useCallback((e) => {
    if (e.pointerType === 'touch') return;          // no hover on touch screens
    last.current = { target: e.target, clientX: e.clientX, clientY: e.clientY };
    if (!frame.current) frame.current = requestAnimationFrame(apply);   // at most once per frame
  }, [apply]);

  const onPointerLeave = useCallback(() => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = 0;
    clear();
  }, [clear]);

  useEffect(() => () => { if (frame.current) cancelAnimationFrame(frame.current); }, []);

  return { mainRef, onPointerMove, onPointerLeave };
}

export default function AppLayout({ title = 'CarbonRoute', children, contentClassName, headerActions }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const glassLight = useGlassLight();

  return (
    <div className="app-shell flex h-[100dvh] overflow-hidden text-foreground">
      {/* Frosted ambient landscape behind everything (fixed, non-interactive) */}
      <AmbientBackground />

      {/* Desktop sidebar — floats as a glass panel (index.css: APP SHELL) */}
      <div className="app-sidebar-slot relative z-10 hidden md:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar (drawer: enters from the left and dismisses to the left) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0 border-r border-border bg-card">
          <Sidebar drawer onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Right side: header + main. min-w-0 lets wide children scroll inside their own box
          instead of widening the whole page (the cause of the horizontal overflow on phones). */}
      <div className="app-column relative z-10 flex min-w-0 flex-1 flex-col">
        <Header title={title} onMenuClick={() => setMobileOpen(true)} actions={headerActions} />
        <main
          ref={glassLight.mainRef}
          data-app-main
          className="flex-1 overflow-y-auto overflow-x-hidden"
          onPointerMove={glassLight.onPointerMove}
          onPointerLeave={glassLight.onPointerLeave}
        >
          <div className={cn('mx-auto w-full max-w-[1400px]', contentClassName ?? 'px-4 py-6 sm:px-6 md:py-8 lg:px-8')}>
            {children}
          </div>
        </main>
      </div>

      {/* Phones: bottom tab bar */}
      <MobileTabBar />
    </div>
  );
}
